import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControlLabel from '@mui/material/FormControlLabel';
import FormGroup from '@mui/material/FormGroup';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Typography from '@mui/material/Typography';
import { useContext, useMemo, useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { SessionContext } from '../../Contexts/SessionContext';
import { TelemetryContext } from '../../Contexts/TelemetryContext';
import DataContainer from '../../Utilities/DataContainer';
import { InputLabel, Select, MenuItem, FormControl, FormHelperText } from '@mui/material';
import surveyDayTimeDropdown from '../../Utilities/Constants/SurveyDayTimeDropdown';
import { TimePair } from '../../Utilities/ViewModels/TimePair';
import { APIContext } from "../../Contexts/APIContext";
import MinMaxParam from "../ViewModels/MinMaxViewModel";
import { useTripRateContext } from "../TripRateContext";
import { getFilteredSurveysForSession } from "../../TripRateNew/TriprateFilters";
import { getValidActiveSurveyRefs } from "../TripRateSurveyRefs";



interface IProps {
    dialogOpen: boolean;
    handleClose: () => void;
}

export default function RankOrderPeriodDialog({ dialogOpen, handleClose }: IProps) {

    const [startTime1, setStartTime1] = useState<number>(24);
    const [endTime1, setEndTime1] = useState<number>(26)
    const [startTime2, setStartTime2] = useState<number>();
    const [endTime2, setEndTime2] = useState<number>();
    const [startTime3, setStartTime3] = useState<number>();
    const [endTime3, setEndTime3] = useState<number>();
    const [showSelection2, setShowSelection2] = useState(false);
    const [showSelection3, setShowSelection3] = useState(false);
    const { sessionState } = useContext(SessionContext);
    const { surveys } = useTripRateContext();
    const { logRankOrderRun } = useContext(TelemetryContext);
    const [errors, setErrors] = useState<{ [key: string]: string }>({});
    const [rankOrderRange, setRankOrderRange] = useState<MinMaxParam>(new MinMaxParam())
    const navigate = useNavigate();
    const { post } = useContext(APIContext);


    useEffect(() => {
        const filteredSurveyRefs = getFilteredSurveysForSession(surveys, sessionState)
            .map(survey => survey.surveyRef);

        post(
            "RankOrder",
            `GetRankOrderMinMaxTimes?hourly=${true}&vehicleType=${sessionState.vehicleType}`,
            getValidActiveSurveyRefs(sessionState.selectedSitesAndSurveys, filteredSurveyRefs),
            setRankOrderRange,
        );
    }, []);

    const isValidRange = (start?: number, end?: number): boolean => {
        return start !== undefined && end !== undefined && end > start;
    };

    const submit = (isPeak: boolean = false) => {
        const newErrors: { [key: string]: string } = {};
        const newPairs: TimePair[] = [];

        if (!isValidRange(startTime1, endTime1)) {
            newErrors["range1"] = "Please enter a valid time range";
        } else {
            newPairs.push({ start: startTime1, end: endTime1 });
        }

        if (showSelection2) {
            if (!isValidRange(startTime2, endTime2)) {
                newErrors["range2"] = "Please enter a valid time range";
            } else {
                newPairs.push({ start: startTime2!, end: endTime2! });
            }
        }

        if (showSelection3) {
            if (!isValidRange(startTime3, endTime3)) {
                newErrors["range3"] = "Please enter a valid time range";
            } else {
                newPairs.push({ start: startTime3!, end: endTime3! });
            }
        }

        if (Object.keys(newErrors).length > 0) {
            setErrors(newErrors);
            return;
        }

        setErrors({});
        sessionState.rankOrderTimes = newPairs.slice(0, 3);
        sessionState.rankOrderPeakHour = isPeak;
        logRankOrderRun(sessionState, { isPeakHour: isPeak, timePairs: newPairs.slice(0, 3) });
        handleClose();
        navigate("/triprate/rankorder");
    };


    const filteredSurveyDayTimes = useMemo(() => {
        return surveyDayTimeDropdown.filter(
            x => x.id >= rankOrderRange.min && x.id <= rankOrderRange.max && x.id % 2 === 0
        );
    }, [rankOrderRange]);


    return (
        <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth >
            <DialogTitle>
                <Stack>
                    <Typography variant={'h5'}>Rank Order</Typography>
                    <FormGroup row>
                        <FormControlLabel control={<Switch value={showSelection2} onChange={() => setShowSelection2(!showSelection2)} />} label="Include 2nd Time Selection" />
                        <FormControlLabel control={<Switch value={showSelection3} onChange={() => setShowSelection3(!showSelection3)} />} label="Include 3rd Time Selection" />
                    </FormGroup>
                </Stack>
            </DialogTitle>
            <DialogContent>
                <Grid container size={12}>
                    <Grid size={6}>
                        <DataContainer title={"Time Selection 1"}>
                            <Grid size={12}>
                                <FormControl fullWidth margin="dense" error={!!errors["range1"]}>
                                    <InputLabel>Start</InputLabel>
                                    <Select
                                        value={startTime1}
                                        label="Start"
                                        onChange={(event) => setStartTime1(Number(event.target.value))}>
                                        {filteredSurveyDayTimes.map(x =>
                                            <MenuItem key={x.id} value={x.id}>
                                                {x.label}
                                            </MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                                <FormControl fullWidth margin="dense" error={!!errors["range1"]}>
                                    <InputLabel>End</InputLabel>
                                    <Select
                                        value={endTime1}
                                        label="End"
                                        onChange={(event) => setEndTime1(Number(event.target.value))}
                                    >
                                        {filteredSurveyDayTimes.map(x =>
                                            <MenuItem key={x.id} value={x.id}>
                                                {x.label}
                                            </MenuItem>
                                        )}
                                    </Select>
                                    {errors["range1"] && (
                                        <FormHelperText>{errors["range1"]}</FormHelperText>
                                    )}
                                </FormControl>
                            </Grid>
                        </DataContainer>
                    </Grid>
                    <Grid size={6}>
                        <DataContainer title={"Site-by-Site Peak Hour"}>
                            <Grid size={12}>
                                <Typography variant={"body2"}>Busiest hour for each site used to calculate rank order</Typography>
                            </Grid>
                            <Grid size={12} textAlign={"right"} >
                                <Button fullWidth={false} onClick={() => submit(true)}>Display</Button>
                            </Grid>
                        </DataContainer>
                    </Grid>
                    {showSelection2 &&
                        <Grid size={6}>
                            <DataContainer title={"Time Selection 2"}>
                                <Grid size={12}>
                                    <FormControl fullWidth margin="dense" error={!!errors["range2"]}>
                                        <InputLabel>Start</InputLabel>
                                        <Select
                                            value={startTime2}
                                            label="Start"
                                            onChange={(event) => setStartTime2(Number(event.target.value))}
                                        >
                                            {filteredSurveyDayTimes.map(x =>
                                                <MenuItem key={x.id} value={x.id}>
                                                    {x.label}
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth margin="dense" error={!!errors["range2"]}>
                                        <InputLabel>End</InputLabel>
                                        <Select
                                            value={endTime2}
                                            label="End"
                                            onChange={(event) => setEndTime2(Number(event.target.value))}
                                        >
                                            {filteredSurveyDayTimes.map(x =>
                                                <MenuItem key={x.id} value={x.id}>
                                                    {x.label}
                                                </MenuItem>
                                            )}
                                        </Select>
                                        {errors["range2"] && (
                                            <FormHelperText>{errors["range2"]}</FormHelperText>
                                        )}
                                    </FormControl>

                                </Grid>
                            </DataContainer>
                        </Grid>
                    }
                    {showSelection3 &&
                        <Grid size={6}>
                            <DataContainer title={"Time Selection 3"}>
                                <Grid size={12}>
                                    <FormControl fullWidth margin="dense" error={!!errors["range3"]}>
                                        <InputLabel>Start</InputLabel>
                                        <Select
                                            value={startTime3}
                                            label="Start"
                                            onChange={(event) => setStartTime3(Number(event.target.value))}
                                        >
                                            {filteredSurveyDayTimes.map(x =>
                                                <MenuItem key={x.id} value={x.id}>
                                                    {x.label}
                                                </MenuItem>
                                            )}
                                        </Select>
                                    </FormControl>
                                    <FormControl fullWidth margin="dense" error={!!errors["range3"]}>
                                        <InputLabel>End</InputLabel>
                                        <Select
                                            value={endTime3}
                                            label="End"
                                            onChange={(event) => setEndTime3(Number(event.target.value))}
                                        >
                                            {filteredSurveyDayTimes.map(x =>
                                                <MenuItem key={x.id} value={x.id}>
                                                    {x.label}
                                                </MenuItem>
                                            )}
                                        </Select>
                                        {errors["range3"] && (
                                            <FormHelperText>{errors["range3"]}</FormHelperText>
                                        )}
                                    </FormControl>
                                </Grid>
                            </DataContainer>
                        </Grid>
                    }

                </Grid>
            </DialogContent>
            <DialogActions sx={{ justifyContent: 'flex-start' }}>
                <Grid container>
                    <Button fullWidth={false} onClick={() => submit(false)}>Confirm Selections</Button>
                    <Button fullWidth={false} onClick={handleClose}>Close</Button>
                    <Button fullWidth={false} onClick={handleClose}>Help</Button>
                </Grid>
            </DialogActions>
        </Dialog>

    );
}
